import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { SetSwitch } from '../SettingsShell/controls';
import { CHANNEL_ICON, CHANNEL_LABEL } from '../../recall-builder/types';
import type { BuilderStep, RecallDraft, StepChannel, StepIssue } from '../../recall-builder/types';
import { channelsFor, gapLabel, maxSteps, tokenPlain } from '../../recall-builder/derive';
import { agentInPlan, usePlan } from '../../team-hold/teamHold';

export interface StepsColumnProps {
  draft: RecallDraft;
  selectedId: string | undefined;
  issues: StepIssue[];
  onSelect: (id: string) => void;
  onAdd: (channel: StepChannel) => void;
  onReorder: (from: number, to: number) => void;
  onOptions: (next: { mentionBenefits?: boolean; sendAlone?: boolean }) => void;
}

/**
 * The steps column (spec 21 §3.3) — the shape of the recall stays visible while
 * one step is being edited, which is why this is beside the editor rather than an
 * accordion that expands to nothing.
 */
export function StepsColumn({
  draft,
  selectedId,
  issues,
  onSelect,
  onAdd,
  onReorder,
  onOptions,
}: StepsColumnProps) {
  const [adding, setAdding] = useState(false);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const aiCallLocked = !agentInPlan('receptionist', usePlan());
  const goal = draft.goal;
  const atMax = goal !== undefined && draft.steps.length >= maxSteps(goal);
  const lastDay = draft.steps[draft.steps.length - 1]?.day ?? -7;

  return (
    <div className="w-[392px] shrink-0 max-[1100px]:w-full">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">The steps</div>

      <div className="mt-2.5 flex flex-col">
        {draft.steps.map((step, i) => {
          const stepIssues = issues.filter((x) => x.stepId === step.id);
          const next = draft.steps[i + 1];
          return (
            <React.Fragment key={step.id}>
              <StepCard
                step={step}
                index={i}
                selected={step.id === selectedId}
                dragging={dragFrom === i}
                issues={stepIssues}
                onSelect={() => { onSelect(step.id); }}
                onDragStart={() => { setDragFrom(i); }}
                onDragEnd={() => { setDragFrom(null); }}
                onDrop={() => {
                  if (dragFrom !== null && dragFrom !== i) onReorder(dragFrom, i);
                  setDragFrom(null);
                }}
              />
              {next ? (
                <div className="py-1 pl-4 text-[11px] text-[var(--text-muted)]">{gapLabel(step, next)}</div>
              ) : null}
            </React.Fragment>
          );
        })}
      </div>

      {/* Add opens INLINE, not a popover — a popover over a column this narrow
          covers the very list you are adding to. */}
      {adding ? (
        <div className="mt-2 rounded-[12px] border border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)] p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[12px] font-semibold text-[var(--text-primary)]">
              Add a step on day {lastDay + 7}
            </span>
            <button
              type="button"
              aria-label="Close"
              onClick={() => { setAdding(false); }}
              className="text-[var(--text-muted)]"
            >
              <Icon kind="x" size={13} stroke={2.2} />
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(goal === undefined ? [] : channelsFor(goal)).map((c) => {
              const locked = c === 'ai_call' && aiCallLocked;
              return (
                <button
                  key={c}
                  type="button"
                  disabled={locked}
                  title={locked ? 'Needs the AI Receptionist' : undefined}
                  onClick={() => {
                    if (locked) return;
                    onAdd(c);
                    setAdding(false);
                  }}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-[9px] border px-2.5 py-1.5 text-[12px] font-semibold',
                    locked
                      ? 'cursor-not-allowed border-[var(--border-default)] bg-[var(--surface-card)] text-[var(--text-muted)] opacity-60'
                      : 'border-[var(--border-default)] bg-[var(--surface-card)] text-[var(--text-secondary)] hover:border-[var(--brand-indigo)]',
                  )}
                >
                  <Icon kind={locked ? 'lock' : (CHANNEL_ICON[c] as IconKind)} size={12} stroke={2} />
                  {locked ? 'AI call · Needs the AI Receptionist' : CHANNEL_LABEL[c]}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={atMax}
          onClick={() => { setAdding(true); }}
          title={atMax ? (goal === 'increase_loyalty' ? 'A loyalty recall is one message' : 'A recall can have at most 8 steps') : undefined}
          className={cn(
            'mt-2 w-full rounded-[12px] border border-dashed px-3 py-2.5 text-[12.5px] font-semibold',
            atMax
              ? 'cursor-not-allowed border-[var(--border-default)] text-[var(--text-muted)]'
              : 'border-[var(--brand-indigo)] text-[var(--brand-indigo)] hover:bg-[var(--brand-indigo-tint)]',
          )}
        >
          + Add a step
        </button>
      )}

      <p className="m-0 mt-3 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
        The recall ends after the last step.{' '}
        <b className="font-semibold text-[var(--text-secondary)]">
          Anyone who books, opts out or stops being a patient leaves straight away.
        </b>
      </p>

      {/* Both off by default. */}
      <div className="mt-4 rounded-[12px] border border-[var(--border-default)] bg-[var(--surface-card)] p-3.5">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">
          Options for this recall
        </div>
        {goal === 'book_appointment' || goal === 'reactivate_patient' ? (
          <OptionRow
            label="Mention expiring benefits"
            desc="Only in the three months before benefits reset, and only for patients whose insurance was checked recently."
            checked={draft.mentionBenefits}
            onChange={(v) => { onOptions({ mentionBenefits: v }); }}
          />
        ) : null}
        <OptionRow
          last
          label="Send on its own"
          desc="Never combine these messages with another one to the same patient."
          checked={draft.sendAlone}
          onChange={(v) => { onOptions({ sendAlone: v }); }}
        />
      </div>
    </div>
  );
}

function OptionRow({
  label,
  desc,
  checked,
  onChange,
  last,
}: {
  label: string;
  desc: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  last?: boolean;
}) {
  return (
    <div
      className="flex items-start justify-between gap-3 py-2.5"
      style={last === true ? undefined : { borderBottom: '1px dotted #DFE3ED' }}
    >
      <span className="min-w-0">
        <span className="block text-[12.5px] font-semibold text-[var(--text-primary)]">{label}</span>
        <span className="mt-0.5 block text-[11px] leading-[1.5] text-[var(--text-muted)]">{desc}</span>
      </span>
      <SetSwitch label={label} checked={checked} onChange={onChange} />
    </div>
  );
}

/** One line of what the step will say, so the column reads without opening one. */
function summarise(step: BuilderStep): string {
  if (step.channel === 'human_call') return 'A call task in Triage Inbox';
  if (step.mode === 'smart') {
    const brief = (step.brief ?? '').trim();
    return brief === '' ? 'No brief yet' : `${brief.slice(0, 64)}${brief.length > 64 ? '…' : ''}`;
  }
  const body = tokenPlain(step.body ?? []);
  return body.trim() === '' ? 'No message yet' : `${body.slice(0, 64)}${body.length > 64 ? '…' : ''}`;
}

function StepCard({
  step,
  index,
  selected,
  dragging,
  issues,
  onSelect,
  onDragStart,
  onDragEnd,
  onDrop,
}: {
  step: BuilderStep;
  index: number;
  selected: boolean;
  dragging: boolean;
  issues: StepIssue[];
  onSelect: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDrop: () => void;
}) {
  // Without the AI Receptionist, an AI-call step stays visible but locked (spec 37 §4).
  const plan = usePlan();
  const aiLocked = step.channel === 'ai_call' && !agentInPlan('receptionist', plan);
  return (
    <div
      draggable
      // Firefox refuses to start a drag without data on the transfer, which is
      // why dragging did nothing in testing (§2.6).
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', step.id);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop();
      }}
      onClick={onSelect}
      className={cn(
        'cursor-pointer rounded-[12px] border p-3',
        dragging && 'opacity-50',
        selected
          ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)]'
          : 'border-[var(--border-default)] bg-[var(--surface-card)] hover:border-[#D6DBEA]',
      )}
    >
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 shrink-0 cursor-grab text-[var(--text-muted)]" aria-hidden>
          <Icon kind="grip" size={13} stroke={1.8} />
        </span>
        <span
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px]',
            selected ? 'bg-[var(--brand-indigo)] text-white' : 'bg-[#F1F3F9] text-[var(--text-muted)]',
          )}
        >
          <Icon kind={CHANNEL_ICON[step.channel] as IconKind} size={13} stroke={2} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-[12.5px] font-bold text-[var(--text-primary)]">
              {CHANNEL_LABEL[step.channel]}
            </span>
            <span className="font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">day {step.day}</span>
            {step.mode !== undefined ? (
              <span
                className={cn(
                  'rounded-[5px] px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.06em]',
                  step.mode === 'smart'
                    ? 'bg-[var(--brand-indigo)] text-white'
                    : 'bg-[#F1F3F9] text-[var(--text-muted)]',
                )}
              >
                {step.mode === 'smart' ? 'Smart' : 'Template'}
              </span>
            ) : null}
            {aiLocked ? (
              <span className="inline-flex items-center gap-1 rounded-[5px] bg-[#F1F3F9] px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                <Icon kind="lock" size={9} stroke={2} /> Needs the AI Receptionist
              </span>
            ) : null}
            <span className="sr-only">Step {index + 1}</span>
          </span>
          <span className="mt-1 block truncate text-[11.5px] text-[var(--text-secondary)]">{summarise(step)}</span>
          {aiLocked ? (
            <span className="mt-1 block text-[11px] text-[var(--text-muted)]">
              Not in your plan — this step is skipped and the next step goes as usual. Talk to the ELVA team
            </span>
          ) : null}
          {/* A red dot and the issue, so an incomplete step is visible from the
              column rather than only when you open it. */}
          {issues.length > 0 ? (
            <span className="mt-1 flex items-center gap-1.5 text-[11px] font-semibold text-[var(--alert-red)]">
              <span className="h-[6px] w-[6px] rounded-full bg-[var(--alert-red)]" />
              {issues[0]?.short}
              {issues.length > 1 ? ` +${String(issues.length - 1)}` : ''}
            </span>
          ) : null}
        </span>
      </div>
    </div>
  );
}
